import React, { useEffect, useState, useRef } from 'react';
import { ArrowRight } from 'lucide-react';

const KineticHero = () => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [showSubheadline, setShowSubheadline] = useState(false);
  const [maxWidth, setMaxWidth] = useState(0);
  const wordWrapperRef = useRef(null);
  
  const wordSequence = ['APIs', 'RAG', 'Databases', 'NLQ', 'Tools', 'Agents', 'Observability', 'OnePlace'];
  
  // Timing: 650-800ms transition + 200ms hold, 900ms hold on last word
  const transitionDuration = 700; // Average of 650-800ms
  const holdDuration = 200;
  const finalHoldDuration = 900;

  // Calculate maximum width needed for the word wrapper
  useEffect(() => {
    if (!wordWrapperRef.current) return;
    
    // Create a temporary span to measure each word
    const tempSpan = document.createElement('span');
    tempSpan.style.cssText = `
      position: absolute;
      visibility: hidden;
      font-size: 80px;
      font-weight: 700;
      font-family: 'Outfit', sans-serif;
      letter-spacing: -0.02em;
      white-space: nowrap;
    `;
    document.body.appendChild(tempSpan);
    
    let maxWordWidth = 0;
    wordSequence.forEach(word => {
      tempSpan.textContent = word;
      const width = tempSpan.offsetWidth;
      if (width > maxWordWidth) {
        maxWordWidth = width;
      }
    });
    
    document.body.removeChild(tempSpan);
    setMaxWidth(maxWordWidth + 20); // Add 20px padding for safety
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      // Skip to final state immediately for reduced motion
      setCurrentWordIndex(7);
      setIsAnimating(false);
      setShowSubheadline(true);
      return;
    }

    if (!isAnimating) return;

    const interval = setInterval(() => {
      setCurrentWordIndex((prev) => {
        if (prev < wordSequence.length - 1) {
          return prev + 1;
        } else {
          // Reached "OnePlace", stop animation and show subheadline
          setIsAnimating(false);
          setTimeout(() => {
            setShowSubheadline(true);
          }, finalHoldDuration);
          return prev;
        }
      });
    }, transitionDuration + holdDuration);

    return () => clearInterval(interval);
  }, [isAnimating, wordSequence.length]);

  const handleBookDemo = () => {
    window.open('https://calendly.com/speakmulti/introduction', '_blank');
  };

  const isFinalWord = currentWordIndex === wordSequence.length - 1;

  return (
    <section className="kinetic-hero">
      <div className="kinetic-container">
        {/* Center-left content */}
        <div className="kinetic-content">
          
          {/* Kinetic Headline */}
          <h1 className="kinetic-headline">
            <span 
              ref={wordWrapperRef}
              className="kinetic-word-wrapper"
              style={{ width: maxWidth > 0 ? `${maxWidth}px` : 'auto' }}
            >
              {wordSequence.map((word, index) => (
                <span
                  key={word}
                  className={`kinetic-word ${
                    index === currentWordIndex ? 'active' : ''
                  } ${index < currentWordIndex ? 'exited' : ''} ${
                    index === wordSequence.length - 1 && isFinalWord ? 'final' : ''
                  }`}
                  data-word={word}
                >
                  {word}
                </span>
              ))}
            </span>
          </h1>

          {/* Subheadline - visible throughout */}
          <p className="kinetic-subheadline visible">
            A common platform to create, manage, deploy and observe tools, agents and context.
          </p>

          {/* CTAs */}
          <div className="kinetic-cta-row visible">
            <button onClick={handleBookDemo} className="btn-primary kinetic-btn-primary">
              Book a demo
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
            <button 
              onClick={() => {
                const element = document.getElementById('platform-solutions');
                if (element) {
                  const headerOffset = 100;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }
              }}
              className="btn-secondary kinetic-btn-secondary"
            >
              Know more
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default KineticHero;
